// src/stages/stage1Plant/environment/FallingBranches.jsx
import React, { useEffect, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

// Snap and Thud Sound FX
const SNAP_SOUNDS = [
  "/stageAssets/stage1-plant/audio/treeSnap1.mp3",
  "/stageAssets/stage1-plant/audio/treeSnap2.mp3",
];

const IMPACT_SOUNDS = [
  "/stageAssets/stage1-plant/audio/treeImpact1.mp3",
  "/stageAssets/stage1-plant/audio/treeImpact2.mp3",
];

// pick one random file
function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// play random file with a simple one-shot playback
function playOneShot(src, volume = 0.8) {
//   console.log("🎧 playOneShot called with:", src);

  const audio = new Audio(src);
  audio.volume = volume;

  audio.play().then(() => {
    // console.log("✅ audio started:", src);
  }).catch((err) => {
    console.warn("❌ audio playback failed:", err);
  });
}

function playRandomSnap(volume = 0.8) {
  playOneShot(pickRandom(SNAP_SOUNDS), volume);
}

function playRandomImpact(volume = 0.9) {
  playOneShot(pickRandom(IMPACT_SOUNDS), volume);
}

function Branch({
  startDelay = 0,
  duration = 1.2,
  startPosition = [0, 3, -2],
  endPosition = [0, 0.1, -2],
  startRotation = [0, 0, 0],
  spin = [2, 1, 1.5],
  restRotation = [0, 0, 0],
  scale = [0.08, 1.2, 0.08],
  visible = false,
  onStart,
  onImpact,
}) {
  const ref = useRef();
  const [startedAt, setStartedAt] = useState(null);
  const hasStarted = useRef(false);
  const hasImpacted = useRef(false);

  useEffect(() => {
    if (!visible) {
      setStartedAt(null);
      hasStarted.current = false;
      hasImpacted.current = false;
      if (ref.current) {
        ref.current.visible = false;
      }
      return;
    }
    console.log("⏱ scheduling branch start in", startDelay, "seconds");
    const timer = setTimeout(() => {
        // play Snap exactly when branch starts / appears
        if (!hasStarted.current) {
            hasStarted.current = true;
            // console.log("🌿 BRANCH START → playing SNAP");
            onStart?.();
        }

        // console.log("🌱 branch visible + falling NOW");

      setStartedAt(performance.now());
      if (ref.current) ref.current.visible = true;
    }, startDelay * 1000);

    return () => clearTimeout(timer);
  }, [visible, startDelay]);

  useFrame(() => {
  if (!ref.current || startedAt === null) return;

  const elapsed = (performance.now() - startedAt) / 1000;
  const t = Math.min(elapsed / duration, 1);

  if (t < 1) {
    // FALLING PHASE
    const easeIn = t * t;

    ref.current.position.lerpVectors(
      new THREE.Vector3(...startPosition),
      new THREE.Vector3(...endPosition),
      easeIn
    );

    ref.current.rotation.x = startRotation[0] + elapsed * spin[0];
    ref.current.rotation.y = startRotation[1] + elapsed * spin[1];
    ref.current.rotation.z = startRotation[2] + elapsed * spin[2];
  } else {
    // IMPACT PHASE
    ref.current.position.set(...endPosition);

    ref.current.rotation.x = THREE.MathUtils.lerp(
      ref.current.rotation.x,
      restRotation[0],
      0.15
    );
    ref.current.rotation.y = THREE.MathUtils.lerp(
      ref.current.rotation.y,
      restRotation[1],
      0.15
    );
    ref.current.rotation.z = THREE.MathUtils.lerp(
      ref.current.rotation.z,
      restRotation[2],
      0.15
    );

    if (!hasImpacted.current) {
        hasImpacted.current = true;
        // console.log("💥 BRANCH IMPACT → playing THUD");
        onImpact?.();
    }
  }
});

  return (
    <group ref={ref} visible={false}>
      <mesh castShadow receiveShadow>
        <cylinderGeometry args={[scale[0], scale[0] * 1.1, scale[1], 8]} />
        <meshStandardMaterial color="#4f3324" roughness={1} />
      </mesh>

      <mesh position={[0.08, 0.2, 0]} rotation={[0, 0, 0.6]} castShadow>
        <cylinderGeometry args={[0.018, 0.018, 0.4, 6]} />
        <meshStandardMaterial color="#43340a" roughness={1} />
      </mesh>
    </group>
  );
}

export default function FallingBranches({
  activateEndTransition = false,
  onPeripheralImpact,
  onFinalImpact,
}) {
  const impactsPlayed = useRef({
    a: false,
    b: false,
    c: false,
    final: false,
  });

  const resetFlags = () => {
    impactsPlayed.current = {
      a: false,
      b: false,
      c: false,
      final: false,
    };
  };

  useEffect(() => {
    if (!activateEndTransition) resetFlags();
  }, [activateEndTransition]);

  const handleImpact = (key, callback) => {
    if (impactsPlayed.current[key]) return;
    impactsPlayed.current[key] = true;
    callback?.();
  };

  return (
    <>
      {/* left side crash */}
      <Branch
        visible={activateEndTransition}
        startDelay={1.0}
        duration={1.0}
        startPosition={[-2.4, 4.4, -2.5]}
        endPosition={[-1.5, 0.15, -1.8]}
        startRotation={[0.4, 0.2, 0.1]}
        spin={[5.0, 1.5, 2.0]}
        restRotation={[1.4, 0.25, 0.8]}
        scale={[0.06, 1.0, 0.06]}
        onStart={() => playRandomSnap(0.72)}
        onImpact={() => {
            playRandomImpact(0.86);
            handleImpact("a", onPeripheralImpact);
        }}
      />

      {/* right side crash */}
      <Branch
        visible={activateEndTransition}
        startDelay={3.2}
        duration={1.0}
        startPosition={[2.2, 4.8, -2.0]}
        endPosition={[1.7, 0.1, -1.2]}
        startRotation={[0.2, -0.3, 0.4]}
        spin={[4.2, 1.2, 2.8]}
        restRotation={[1.15, -0.2, -0.55]}
        scale={[0.07, 1.2, 0.07]}
        onStart={() => playRandomSnap(0.72)}
        onImpact={() => {
            playRandomImpact(0.86);
            handleImpact("b", onPeripheralImpact);
        }}
    />

      {/* near foreground side crash */}
      <Branch
        visible={activateEndTransition}
        startDelay={5.2}
        duration={0.9}
        startPosition={[-1.0, 4.6, 2.2]}
        endPosition={[-0.95, 0.04, 2.2]}
        startRotation={[0., 0.1, -0.2]}
        spin={[6.0, 0.8, 1.8]}
        restRotation={[1.55, 0.1, -0.5]}
        scale={[0.05, 0.9, 0.05]}
        onStart={() => playRandomSnap(0.72)}
        onImpact={() => {
            playRandomImpact(0.86);
            handleImpact("c", onPeripheralImpact);
        }}
      />

      {/* killer branch */}
      <Branch
        visible={activateEndTransition}
        startDelay={6.8}
        duration={0.7}
        startPosition={[0.08, 4.8, 0.18]}
        endPosition={[0.02, 0.55, 0.05]}
        startRotation={[0.1, 0.2, 0.1]}
        spin={[9.0, 2.4, 1.5]}
        restRotation={[1.7, 0.15, 0.05]}
        scale={[0.13, 2.2, 0.13]}
        onStart={() => playRandomSnap(0.95)}
        onImpact={() => {
          playRandomImpact(1.0);
          handleImpact("final", onFinalImpact);
        }}
      />
    </>
  );
}